import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BaseError } from "src/contexts/_shared/domain/errors/BaseError";
import { useDocumentationArea } from "./useDocumentationArea";
import { DocumentationArea } from "src/contexts/documentationArea/domain/DocumentationArea";
import { DocumentationAreaNotFoundError } from "src/contexts/documentationArea/domain/errors/DocumentationAreaNotFoundError";

type Context = {
  previous: DocumentationArea;
  next: DocumentationArea;
};

export const useDocumentationAreaMutation = <TData, TInput>({
  mutationFn,
  optimisticUpdate,
  onSettled,
}: {
  mutationFn: (v: any) => Promise<any>;
  optimisticUpdate?: (input: TInput, previous: DocumentationArea) => DocumentationArea;
  onSettled?: () => Promise<void>;
}) => {
  const queryClient = useQueryClient();
  const { documentationArea, setIsDocumentationAreaQueryEnabled } = useDocumentationArea();
  if (!documentationArea) throw new DocumentationAreaNotFoundError();

  const documentationAreaId = documentationArea.getId();
  const { mutate, data, isSuccess, isError, isPending } = useMutation<
    TData,
    BaseError,
    TInput,
    Context
  >({
    mutationFn,
    onMutate: async (input) => {
      setIsDocumentationAreaQueryEnabled(false);
      await queryClient.cancelQueries({ queryKey: ["DocumentationArea", documentationAreaId] });

      const previous: DocumentationArea | undefined = queryClient.getQueryData([
        "DocumentationArea",
        documentationAreaId,
      ]);

      if (!previous) return;

      let next = new DocumentationArea({
        id: previous.getId(),
        type: previous.getType(),
        actor: previous.getActor(),
        availableActors: previous.getAvailableActors(),
        locale: previous.getLocale(),
      });

      if (optimisticUpdate) next = optimisticUpdate(input, previous);

      queryClient.setQueryData(["DocumentationArea", documentationAreaId], next);

      return { previous, next };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(["DocumentationArea", documentationAreaId], context?.previous);
    },
    onSettled: async () => {
      setIsDocumentationAreaQueryEnabled(true);
      if (onSettled) await onSettled();
      queryClient.invalidateQueries({ queryKey: ["DocumentationArea", documentationAreaId] });
    },
  });

  return { mutate, data: data as TData, isSuccess, isError, isPending };
};
